import {useTranslation} from 'react-i18next'

import {monthsByIdx} from 'features/common/core/constants'
import DateSelect from './components/DateSelect'
import {useEffect, useState} from 'react'

interface DateInputProps {
    value: string | null
    onChange: (date: string) => void
}

const DateInput = ({value, onChange}: DateInputProps) => {
    const {t} = useTranslation()

    const [day, setDay] = useState<number | null>(null)
    const [month, setMonth] = useState<number | null>(null)
    const [year, setYear] = useState<number | null>(null)

    const daysInCurrentMonth = getDaysInMonth({
        month: month || 0,
        year: year || 2023,
    })

    const yearSelectOptions = getYearSelectOptions()
    const fullMonthDaysArray = getDaySelectOptions(daysInCurrentMonth)
    const monthOptions = getMonthSelectOptions()

    useEffect(() => {
        if (value) {
            const date = new Date(value)
            setDay(date.getUTCDate())
            setMonth(date.getUTCMonth())
            setYear(date.getUTCFullYear())
        }
    }, [value])

    const onChangeDay = (value: number) => {
        setDay(value)
        if (value && month && year) {
            const ISODate = combineDateValues({day: value, month, year})
            onChange(ISODate)
        }
    }

    const onChangeMonth = (value: number) => {
        setMonth(value)
        if (day && value) {
            const daysThisMonth = getDaysInMonth({
                month: value,
                year: year || 2020,
            })
            const isDayImpossible = daysThisMonth < day
            const newDayValue = isDayImpossible ? daysThisMonth : day

            if (isDayImpossible) {
                setDay(newDayValue)
            }

            if (year) {
                const ISODate = combineDateValues({
                    day: newDayValue,
                    month: value,
                    year,
                })
                onChange(ISODate)
            }
        }
    }

    const onChangeYear = (value: number) => {
        setYear(value)
        if (day && month && value) {
            const daysThisMonth = getDaysInMonth({month, year: value})
            const newDayValue = daysThisMonth < day ? daysThisMonth : day
            const ISODate = combineDateValues({
                day: newDayValue,
                month,
                year: value,
            })
            onChange(ISODate)
        }
    }

    return (
        <div className="grid w-[20.625rem] grid-cols-12 gap-2">
            <div className="col-span-3">
                <DateSelect
                    placeholder={t('QR_EMPLOYEE_MODAL_BIRTH_PLACEHOLDER_DAY')}
                    options={fullMonthDaysArray}
                    value={day}
                    onChange={onChangeDay}
                />
            </div>
            <div className="col-span-5">
                <DateSelect
                    placeholder={t('QR_EMPLOYEE_MODAL_BIRTH_PLACEHOLDER_MONTH')}
                    options={monthOptions}
                    value={month}
                    onChange={onChangeMonth}
                />
            </div>
            <div className="col-span-4">
                <DateSelect
                    placeholder={t('QR_EMPLOYEE_MODAL_BIRTH_PLACEHOLDER_YEAR')}
                    options={yearSelectOptions}
                    value={year}
                    onChange={onChangeYear}
                />
            </div>
        </div>
    )
}

const getDaysInMonth = ({month, year}: {month: number; year: number}) => {
    const date = new Date(year, month + 1, 0)
    return date.getDate()
}

const getDaySelectOptions = (daysInMonth: number) => {
    return new Array(daysInMonth).fill(null).map((i, idx) => {
        const day = idx + 1
        return {
            value: day,
            label: day.toString(),
        }
    })
}

const getMonthSelectOptions = () => {
    return new Array(12).fill(null).map((_, idx) => ({
        value: idx,
        label: monthsByIdx[idx as keyof typeof monthsByIdx],
    }))
}

const getYearSelectOptions = () => {
    const currentYear = new Date().getFullYear()
    return new Array(100).fill(null).map((i, idx) => {
        const year = currentYear - idx
        return {
            value: year,
            label: year.toString(),
        }
    })
}

const combineDateValues = ({
    day,
    month,
    year,
}: {
    day: number
    month: number
    year: number
}) => {
    const date = new Date(Date.UTC(year, month, day))
    return date.toISOString()
}

export default DateInput
